import { inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, createAction, Store } from '@ngrx/store';
import { EMPTY, forkJoin, from, Observable, of } from 'rxjs';
import { catchError, filter, map, mergeMap, switchMap, take, withLatestFrom } from 'rxjs/operators';

import {
  billAccountUpdateDeliveryPreferenceFail,
  billAccountUpdateDeliveryPreferenceSuccess
} from '@amfam/billing/billaccount/data-access/lib/+state/billaccount.actions';
import { SummariesLoadSuccess } from '@amfam/policy/data-access/lib/+state/summaries/summaries.actions';
import {
  EnrollGoPaperlessFailure,
  EnrollGoPaperlessSuccess,
  UnEnrollGoPaperlessFailure,
  UnEnrollGoPaperlessSuccess
} from '@amfam/policy/go-paperless/data-access/lib/+state/go-paperless.actions';
import { GenerateEmailConstants } from '@amfam/policy/go-paperless/data-access/lib/models/go-paperless.model';
import { GoPaperlessService } from '@amfam/policy/go-paperless/data-access/lib/services/go-paperless.service';
import { usesPolicyDocPreferences } from '@amfam/policy/models/lib/models/policy-doc-preferences';
import { selectCustomerGroupId } from '@amfam/shared/utility/brand/lib/+state/brand.selectors';
import { Partners } from '@amfam/shared/utility/brand/lib/services/brand.enum';

import { RetrievePreferencFromExpAPIResponse } from '../models/user-account-preferences.model';
import { UserAccountPreferencesService } from '../service/user-account-preferences.service';
import {
  loadPreferences,
  loadPreferencesFailure,
  loadPreferencesSuccess,
  prepareEmail,
  sendPaperlessEmail,
  sendPaperlessEmailFailure,
  sendPaperlessEmailSuccess,
  updatePreferences,
  updatePreferencesFailure,
  updatePreferencesSuccess
} from './preferences.actions';

// Effect to trigger loading preferences for each policy
export const triggerLoadPreferences = createEffect(
  (actions$ = inject(Actions)) => {
    return actions$.pipe(
      ofType(SummariesLoadSuccess),
      map(action => action.payload),
      switchMap(payload => {
        if (!payload || payload.length === 0) {
          return EMPTY;
        }

        // Only policies whose document delivery is set per policy (Horison, Phoenix, Connect umbrella)
        const filteredPayload = payload.filter(item => usesPolicyDocPreferences(item.sourcePath));

        // Dispatch loadPreferences action for each filtered policy
        return from(filteredPayload).pipe(
          map(item => loadPreferences({ policyNumber: item.policyNumber }))
        );
      })
    );
  },
  { functional: true }
);

// Effect to handle the actual API call for loading
export const loadPreferencesEffect = createEffect(
  (
    actions$ = inject(Actions),
    userAccountPreferencesService = inject(UserAccountPreferencesService)
  ) => {
    return actions$.pipe(
      ofType(loadPreferences),
      mergeMap(({ policyNumber }) =>
        userAccountPreferencesService.getPreferencesFromExpApi(policyNumber).pipe(
          map((response: RetrievePreferencFromExpAPIResponse) => {
            return loadPreferencesSuccess({ response });
          }),
          catchError(error => of(loadPreferencesFailure({ policyNumber, error })))
        )
      )
    );
  },
  { functional: true }
);

// Effect to handle updating preferences
export const updatePreferencesEffect = createEffect(
  (
    actions$ = inject(Actions),
    userAccountPreferencesService = inject(UserAccountPreferencesService)
  ) => {
    return actions$.pipe(
      ofType(updatePreferences),
      mergeMap(({ policyId, preferenceOption }) =>
        userAccountPreferencesService
          .updateUserAccountPreferencesFromExpApi(policyId, preferenceOption)
          .pipe(
            map(() => {
              return updatePreferencesSuccess({
                policyId,
                preferenceOption
              });
            }),
            catchError(error => of(updatePreferencesFailure({ policyId, error })))
          )
      )
    );
  },
  { functional: true }
);

export const shouldSendEmail = createEffect(
  (actions$ = inject(Actions), store = inject(Store)) => {
    return actions$.pipe(
      ofType(updatePreferencesSuccess),
      withLatestFrom(store.select(selectCustomerGroupId)),
      filter(([_, customerGroupId]) => customerGroupId === Partners.LEMONADE),
      map(([action]) =>
        sendPaperlessEmail({
          payload: {
            paperlessOperation:
              action.preferenceOption === 'PAPER'
                ? GenerateEmailConstants.GoPaperlessOptOut
                : GenerateEmailConstants.GoPaperlessOptIn
          }
        })
      )
    );
  },
  { functional: true }
);

export const prepareEmailEffect = createEffect(
  (actions$ = inject(Actions), store = inject(Store)) => {
    return actions$.pipe(
      ofType(prepareEmail),
      withLatestFrom(store.select(selectCustomerGroupId)),
      filter(([_, customerGroupId]) => customerGroupId === '642'),
      switchMap(([action]) => {
        const policyPreferences = action.preferenceOptions.policy;
        const billingPreferences = action.preferenceOptions.billing;
        const userAccountPreferences = action.preferenceOptions.userAccount;
        const allPolicyPreferences = [
          ...(policyPreferences || []),
          ...(userAccountPreferences || [])
        ];

        let templateCode = determinePaperlessScenario(billingPreferences, allPolicyPreferences);
        const actionsTowaitFor: Observable<Action>[] = [];

        if ((userAccountPreferences || []).length) {
          actionsTowaitFor.push(
            actions$.pipe(ofType(updatePreferencesSuccess, updatePreferencesFailure), take(1))
          );
        }

        if ((policyPreferences || []).length > 0) {
          actionsTowaitFor.push(
            actions$.pipe(
              ofType(
                EnrollGoPaperlessSuccess,
                EnrollGoPaperlessFailure,
                UnEnrollGoPaperlessSuccess,
                UnEnrollGoPaperlessFailure
              ),
              take(1)
            )
          );
        }

        if ((billingPreferences || []).length > 0) {
          actionsTowaitFor.push(
            actions$.pipe(
              ofType(
                billAccountUpdateDeliveryPreferenceSuccess,
                billAccountUpdateDeliveryPreferenceFail
              ),
              take(1)
            )
          );
        }
        return forkJoin(actionsTowaitFor).pipe(
          map(results => {
            const failures = results.filter(
              r => r.type.includes('Fail') || r.type.includes('Failure')
            );
            failures.forEach(f => {
              if (f.type.includes('BillAccount Update Delivery Preference Fail')) {
                templateCode = determinePaperlessScenario([], allPolicyPreferences);
              }
              if (
                f.type.includes('Enroll GoPaperless Failure') ||
                f.type.includes('Unenroll GoPaperless Failure')
              ) {
                templateCode = determinePaperlessScenario(action.preferenceOptions.billing, []);
              }
            });
            if (templateCode !== 'error') {
              return sendPaperlessEmail({
                payload: {
                  paperlessOperation: templateCode,
                  affectedItemsList: [],
                  correlationId: action.preferenceOptions.correlationId
                }
              });
            } else {
              return createAction('[Empty] Empty Action')();
            }
          })
        );
      })
    );
  },
  { functional: true }
);

function determinePaperlessScenario(
  billingPreferences: string[] | undefined,
  allPolicyPreferences: string[] | undefined
): string {
  billingPreferences = billingPreferences?.map(s => s.toLowerCase());
  allPolicyPreferences = allPolicyPreferences?.map(s => s.toLowerCase());
  if (!billingPreferences?.length && !allPolicyPreferences?.length) {
    return 'error';
  }
  if (billingPreferences?.includes('email') && billingPreferences?.includes('paper')) {
    return 'OptIn_OptOut_Both_Other';
    //Scenario # 8 - "Both" Option for Billing Document OR "Both" Option for Billing Document + Email or Paper option for other Partners
  }

  if (allPolicyPreferences?.includes('email') && allPolicyPreferences?.includes('paper')) {
    //Scenario # 8 - "Both" Option for Billing Document OR "Both" Option for Billing Document + Email or Paper option for other Partners
    return 'OptIn_OptOut_Both_Other';
  }

  if (billingPreferences?.includes('email') && allPolicyPreferences?.includes('email')) {
    //Scenario # 3 - Opt-in, both Policy and Billing Email Program
    return 'PaperlessOptin_Both';
  }

  if (billingPreferences?.includes('email') && allPolicyPreferences?.includes('paper')) {
    //Scenario # 7 - Both, Paperless and Paper Enrollment
    return 'Optin_OptOut_Both';
  }

  if (billingPreferences?.includes('paper') && allPolicyPreferences?.includes('email')) {
    //Scenario # 7 - Both, Paperless and Paper Enrollment
    return 'Optin_OptOut_Both';
  }

  if (
    allPolicyPreferences?.includes('email') &&
    (!billingPreferences || billingPreferences.length === 0)
  ) {
    //Scenario # 1 - Opt-in Policy Email Program
    return 'PaperlessOptin_Policy';
  }

  if (
    billingPreferences?.includes('email') &&
    (!allPolicyPreferences || allPolicyPreferences.length === 0)
  ) {
    //Scenario # 2 - Opt-in Billing Email Program
    return 'PaperlessOptin_Billing';
  }

  if (billingPreferences?.includes('paper') && allPolicyPreferences?.includes('paper')) {
    //Scenario # 6 - Opt-out, both Policy and Billing Email Program
    return 'PaperlessOptOut_Both';
  }

  if (
    allPolicyPreferences?.includes('paper') &&
    (!billingPreferences || billingPreferences.length === 0)
  ) {
    //Scenario # 4 - Opt-out Policy Email Program
    return 'PaperlessOptOut_Policy';
  }

  if (
    billingPreferences?.includes('paper') &&
    (!allPolicyPreferences || allPolicyPreferences.length === 0)
  ) {
    //Scenario # 5 - Opt-out Billing Email Program
    return 'PaperlessOptOut_Billing';
  }
  return 'OptIn_OptOut_Both_Other';
  //Scenario # 8 - "Both" Option for Billing Document OR "Both" Option for Billing Document + Email or Paper option for other Partners
}

export const generateEmail = createEffect(
  (actions$ = inject(Actions), goPaperlessService = inject(GoPaperlessService)) => {
    return actions$.pipe(
      ofType(sendPaperlessEmail),
      map(action => action.payload),
      mergeMap(payload => {
        return goPaperlessService.generateCustomerEmail(payload).pipe(
          map(response => sendPaperlessEmailSuccess({ response })),
          catchError(error => of(sendPaperlessEmailFailure({ error })))
        );
      })
    );
  },
  { functional: true }
);
